import { useState, useEffect } from 'react';
import subscriptionService from '../services/subscriptionService';

/**
 * Hook to manage itinerary limits and subscription data
 */
const useItineraryLimits = () => {
  const [usage, setUsage] = useState(null);
  const [canCreate, setCanCreate] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recommendations, setRecommendations] = useState(null);

  useEffect(() => {
    fetchLimitsData();
  }, []);

  const fetchLimitsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch usage data - backend returns { success: true, data: {...} }
      const usageResponse = await subscriptionService.getCurrentUsage();
      
      // Handle response structure properly
      const usageData = usageResponse?.data || {};
      
      // Set usage with guaranteed structure
      const normalizedUsage = {
        total_itineraries: usageData.total_itineraries || 0,
        monthly_itineraries: usageData.monthly_itineraries || 0,
        monthly_limit: usageData.monthly_limit || 5,
        annual_limit: usageData.annual_limit || 60,
        plan_name: usageData.plan_name || 'free',
        can_create: usageData.can_create !== undefined ? usageData.can_create : true,
        usage_percentage: usageData.usage_percentage || 0
      };
      
      setUsage(normalizedUsage);
      setCanCreate(normalizedUsage.can_create);

      // Fetch recommendations if usage is high
      if (normalizedUsage.usage_percentage >= 80) {
        try {
          const recResponse = await subscriptionService.getUpgradeRecommendations();
          const recData = recResponse?.data || {};
          
          // Normalize recommendations
          const normalizedRecs = {
            should_upgrade: recData.should_upgrade || false,
            current_plan: recData.current_plan || normalizedUsage.plan_name,
            recommended_plan: recData.recommended_plan || 'starter',
            reason: recData.reason || 'Consider upgrading for more itineraries',
            usage_percentage: recData.usage_percentage || normalizedUsage.usage_percentage
          };
          
          setRecommendations(normalizedRecs);
        } catch (recErr) {
          console.error('Failed to fetch recommendations:', recErr);
          // Don't fail the whole operation if recommendations fail
        }
      }
    } catch (err) {
      console.error('Failed to fetch limits data:', err);
      setError(err);
      
      // Set default safe values on error
      setUsage({
        total_itineraries: 0,
        monthly_itineraries: 0,
        monthly_limit: 5,
        annual_limit: 60,
        plan_name: 'free',
        can_create: true,
        usage_percentage: 0
      });
      setCanCreate(true);
    } finally {
      setLoading(false);
    }
  };

  const checkCanCreate = async () => {
    try {
      const response = await subscriptionService.canCreateItinerary();
      const responseData = response?.data || {};
      const canCreateValue = responseData.canCreate !== undefined ? responseData.canCreate : true;
      setCanCreate(canCreateValue);
      return canCreateValue;
    } catch (err) {
      console.error('Failed to check itinerary creation permission:', err);
      return true; // Default to true on error to not block users
    }
  };

  const refreshLimits = () => {
    fetchLimitsData();
  };

  return {
    usage,
    canCreate,
    loading,
    error,
    recommendations,
    checkCanCreate,
    refreshLimits
  };
};

export default useItineraryLimits;
